import { Fragment } from 'common-tokenizer';
import React, { useState } from 'react';

import { css, styled, theme } from '../stitches.config';
import { shadowSize } from '../tokens/shadows';
import { BasicButton } from './basic-button';
import { InputError } from './input-error';
import { InputLabel } from './input-label';
import { Box } from './layout';
import { Spacer } from './spacer';

export type Props = {
  name: string;
  label?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  noError?: boolean;
  error?: string;
  onChange?: (ev: React.BaseSyntheticEvent) => void;
  inputImage?: Fragment;
} & React.ComponentProps<typeof SimpleInput>;

export const Input = React.forwardRef<HTMLInputElement, Props>(
  ({ label, noError, error, css, inputImage, ...props }, ref) => {
    const inputElement = 'input';
    const [passwordShown, setPasswordShown] = useState(false);

    const togglePassword = () => {
      setPasswordShown(!passwordShown);
    };
    return (
      <Box css={{ display: 'flex', flexDirection: 'column' }}>
        {label ? (
          <>
            <InputLabel
              disabled={props.disabled}
              htmlFor={props.id || props.name}
            >
              {label}
              {props.type === 'password' ? (
                <BasicButton
                  type="button"
                  tabIndex={-1}
                  onClick={togglePassword}
                  id="toggle-password-button"
                >
                  {passwordShown ? 'Hide password' : 'Show password'}
                </BasicButton>
              ) : null}
            </InputLabel>
            <Spacer height={8} />
          </>
        ) : null}
        <SimpleInput
          id={props.name}
          as={inputElement}
          defaultValue={props.value === undefined ? '' : undefined}
          {...props}
          state={error ? 'error' : 'default'}
          ref={ref}
          type={
            props.type === 'password'
              ? passwordShown
                ? 'text'
                : 'password'
              : props.type
          }
        />
        {inputImage ?? <></>}
        {noError ? <></> : <InputError error={error} />}
      </Box>
    );
  },
);

export const inputCss = css({
  variants: {
    state: {
      default: {
        $$focusShadowColor: theme.colors.blueAlpha7,
        $$focusBorderColor: theme.colors.blue8,
        $$idleShadowColor: theme.colors.grayAlpha7,
        $$idleBorderColor: theme.colors.gray8,
        $$backgroundColor: theme.colors.gray1,
        $$placeholderColor: theme.colors.gray9,
      },
      error: {
        $$focusShadowColor: theme.colors.redAlpha7,
        $$focusBorderColor: theme.colors.red11,
        $$idleShadowColor: theme.colors.redAlpha7,
        $$idleBorderColor: theme.colors.red8,
        $$backgroundColor: theme.colors.red1,
        $$placeholderColor: theme.colors.red6,
      },
    },
  },

  flexGrow: 1,
  borderStyle: 'solid',
  borderWidth: '1px',
  backgroundColor: theme.colors.gray1,
  borderRadius: '8px',
  fontFamily: 'inherit',
  padding: '12px 16px',
  margin: '0',
  outline: 'none',
  appearance: 'none',
  borderColor: '$$idleBorderColor',
  boxShadow: `${shadowSize[200]} $$idleShadowColor`,
  '&:focus': {
    boxShadow: `${shadowSize[200]} $$focusShadowColor`,
    borderColor: '$$focusBorderColor',
  },
  '&:focus:hover': {
    boxShadow: `${shadowSize[300]} $$focusShadowColor`,
  },
  '&:focus:active': {
    boxShadow: `${shadowSize[100]} $$focusShadowColor`,
  },
  '&:hover': {
    boxShadow: `${shadowSize[300]} $$idleShadowColor`,
  },
  '&:active': {
    boxShadow: `${shadowSize[100]} $$idleShadowColor`,
  },
  '&:disabled': {
    backgroundColor: theme.colors.gray2,
    borderColor: theme.colors.gray6,
    boxShadow: 'none',
    pointerEvents: 'none',
    '&::placeholder': {
      color: theme.colors.gray6,
    },
  },
  '&::placeholder': {
    color: 'rgba(93, 106, 129, 1)',
  },

  defaultVariants: {
    state: 'default',
  },
});

export const SimpleInput = styled('input', inputCss);
